import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { RefreshCw, Download, Hotel, CreditCard, Calendar } from 'lucide-react';

const OptimizeFilters = ({
  roomTypes,
  ratePlans,
  selectedRoomType,
  selectedRatePlan,
  days,
  onRoomTypeChange,
  onRatePlanChange,
  onDaysChange,
  onRefresh,
  onExport,
  loading
}) => {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Room Type Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Hotel className="h-4 w-4" />
              Room Type
            </Label>
            <Select value={selectedRoomType} onValueChange={onRoomTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select room type" />
              </SelectTrigger>
              <SelectContent>
                {roomTypes.map((roomType) => (
                  <SelectItem key={roomType.id} value={roomType.id}>
                    {roomType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rate Plan Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Base Rate Plan
            </Label>
            <Select 
              value={selectedRatePlan} 
              onValueChange={onRatePlanChange}
              disabled={!ratePlans.length}
            >
              <SelectTrigger>
                <SelectValue placeholder={ratePlans.length ? "Select rate plan" : "No rate plans"} />
              </SelectTrigger>
              <SelectContent>
                {ratePlans.map((ratePlan) => (
                  <SelectItem key={ratePlan.id} value={ratePlan.id}>
                    {ratePlan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Days Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Forecast Days
            </Label>
            <Select value={days.toString()} onValueChange={(val) => onDaysChange(parseInt(val))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days</SelectItem>
                <SelectItem value="365">365 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <Label className="opacity-0">Actions</Label>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="default"
                onClick={onRefresh}
                disabled={loading || !selectedRoomType}
                className="flex-1"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="default"
                onClick={onExport}
                disabled={loading || !selectedRoomType}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Info Text */}
        {selectedRoomType && (
          <div className="mt-4 text-sm text-muted-foreground">
            Showing ML-powered pricing recommendations for{' '}
            <span className="font-medium text-foreground">
              {roomTypes.find(rt => rt.id === selectedRoomType)?.name}
            </span>
            {selectedRatePlan && ratePlans.length > 0 && (
              <>
                {' '}with{' '}
                <span className="font-medium text-foreground">
                  {ratePlans.find(rp => rp.id === selectedRatePlan)?.name}
                </span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OptimizeFilters;
